import { alpha, createTheme, darken } from '@mui/material'

// Color setup
const mainColor = '#5D3FD3' // A nice violet color

const darkBackground = '#121212' // Standard dark theme background
const darkPrimaryMain = '#A78BFA' // Lighter violet for dark mode
const darkPrimaryLight = '#C4B5FD' // Even lighter for highlights
const darkSecondary = '#FBBF24' // Amber for secondary actions
const darkError = '#F87171' // A soft red
const darkWarning = '#FBBF24' // Amber
const darkInfo = '#60A5FA' // A soft blue
const darkSuccess = '#34D399' // A soft green

const lightBackground = '#F8F9FA' // A very light grey
const lightPrimaryMain = '#5D3FD3' // The main violet color
const lightPrimaryLight = '#8B5CF6' // A lighter violet
const lightSecondary = '#F59E0B' // Amber for secondary actions
const lightError = '#EF4444' // A standard red
const lightWarning = '#F59E0B' // Amber
const lightInfo = '#3B82F6' // A standard blue
const lightSuccess = '#10B981' // A standard green
//

export const logInBackgroundImage = `linear-gradient(135deg, ${lightPrimaryMain} 0%, ${lightPrimaryLight} 100%)`

const darkPalette = {
  background: { default: darkBackground, paper: '#1E1E1E' },
  primary: { main: darkPrimaryMain, light: darkPrimaryLight },
  secondary: { main: darkSecondary },
  error: { main: darkError },
  warning: { main: darkWarning },
  info: { main: darkInfo },
  success: { main: darkSuccess },
  mode: 'dark',
}

const lightPalette = {
  background: { default: lightBackground, paper: '#FFFFFF' },
  primary: { main: lightPrimaryMain, light: lightPrimaryLight },
  secondary: { main: lightSecondary },
  error: { main: lightError },
  warning: { main: lightWarning },
  info: { main: lightInfo },
  success: { main: lightSuccess },
  text: {
    primary: '#212529',
    secondary: '#495057',
  },
  mode: 'light',
}

const componentsOverrides = (theme) => ({
  MuiAppBar: {
    styleOverrides: {
      root: {
        boxShadow: 'none',
        borderBottom: `1px solid ${theme.palette.divider}`,
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: theme.shape.borderRadius,
      },
      input: {
        padding: `${theme.spacing(1.5)} ${theme.spacing(2)}`,
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: theme.shape.borderRadius,
        textTransform: 'none',
        fontWeight: 600,
      },
      containedPrimary: {
        '&:hover': {
          backgroundColor: darken(theme.palette.primary.main, 0.1),
        },
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        padding: '12px 20px',
        minHeight: 48,
        borderRadius: theme.shape.borderRadius,
        transition: 'color .2s',
        '&.MuiButtonBase-root': {
          minWidth: 'auto',
          marginRight: theme.spacing(1),
        },
        '&.Mui-selected, &.Mui-selected:hover': {
          color: theme.palette.primary.contrastText,
          backgroundColor: theme.palette.primary.main,
          zIndex: 5,
        },
        '&:hover': {
          color: theme.palette.primary.main,
        },
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: {
        minHeight: 48,
        overflow: 'visible',
      },
      indicator: {
        display: 'none',
      },
    },
  },
  MuiTextField: {
    defaultProps: {
      variant: 'outlined',
    },
  },
  RaAppBar: {
    styleOverrides: {
      root: {
        color: theme.palette.text.primary,
        '& .RaAppBar-toolbar': {
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
        },
      },
    },
  },
  RaMenuItemLink: {
    styleOverrides: {
      root: {
        padding: '10px 15px',
        margin: `0 ${theme.spacing(1)}px`,
        borderRadius: theme.shape.borderRadius,
        '&:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
        },
        '&.RaMenuItemLink-active': {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          '& .MuiSvgIcon-root': {
            color: theme.palette.primary.contrastText,
          },
        },
      },
    },
  },
})

const createCustomTheme = (palette) => {
  const themeOptions = {
    palette,
    shape: { borderRadius: 8 },
    sidebar: { width: 240, closedWidth: 65 },
    spacing: 8,
    typography: {
      fontFamily: 'Gabarito, "Helvetica Neue", Arial, sans-serif',
      h1: { fontWeight: 700, fontSize: '3.5rem' },
      h2: { fontWeight: 700, fontSize: '3rem' },
      h3: { fontWeight: 700, fontSize: '2.5rem' },
      h4: { fontWeight: 700, fontSize: '2rem' },
      h5: { fontWeight: 700, fontSize: '1.5rem' },
      h6: { fontWeight: 600, fontSize: '1.25rem' },
      button: { textTransform: 'none', fontWeight: 700 },
    },
  }
  const theme = createTheme(themeOptions)
  theme.components = componentsOverrides(theme)
  return theme
}

export const customLightTheme = createCustomTheme(lightPalette)
export const customDarkTheme = createCustomTheme(darkPalette)
